import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Home,
  LogOut,
  User,
  LogIn,
  UserPlus,
  Users,
  Settings,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import Logo from "@/components/Logo";
import { useAuth } from "@/contexts/AuthContext";
import { logger } from "@/lib/logger";

const Navigation = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAuthenticated, logout } = useAuth();

  const navItems = [
    { path: "/", label: "My Tabs", icon: Home },
    { path: "/member-lookup", label: "Teammate Lookup", icon: Users },
  ];

  const handleLogout = async (e) => {
    e.preventDefault();
    try {
      await logout();
    } catch (error) {
      logger.error("Logout failed", { error: error.message });
    } finally {
      navigate("/login");
    }
  };

  // Get user initials for avatar
  // Get user initials for avatar
  const getUserInitials = () => {
    if (user?.given_name && user?.family_name) {
      return `${user.given_name.charAt(0)}${user.family_name.charAt(0)}`.toUpperCase();
    }
    if (user?.name) {
      return user.name.charAt(0).toUpperCase();
    }
    if (!user?.email) return "U";
    return user.email.charAt(0).toUpperCase();
  };

  const getUserName = () => {
    if (user?.given_name && user?.family_name) {
      return `${user.given_name} ${user.family_name}`;
    }
    return user?.name || user?.email || "User";
  };

  return (
    <nav className="sticky top-0 z-50 backdrop-blur-md bg-white/80 dark:bg-slate-900/70 border-b border-slate-200/60 dark:border-slate-700/40">
      <div className="mx-auto flex max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8 h-16">
        <div className="flex items-center">
          <Logo
            className="h-8 w-auto"
            onClick={() => navigate("/")}
            data-testid="nav-logo"
          />
        </div>

        <div className="flex items-center gap-4">
          {/* Navigation Items - Hidden on mobile */}
          <div className="hidden h-16 items-center gap-2 md:flex">
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              return (
                <Button
                  key={item.path}
                  onClick={() => navigate(item.path)}
                  variant={isActive ? "default" : "ghost"}
                  className={`flex items-center gap-2 px-4 py-2 ${
                    isActive
                      ? "shadow-card"
                      : "text-slate-600 hover:text-brand dark:text-slate-300 dark:hover:text-brand-light"
                  }`}
                  data-testid={`nav-${item.label.toLowerCase()}`}
                >
                  <Icon className="h-4 w-4" />
                  {item.label}
                </Button>
              );
            })}
          </div>

          {/* User Menu */}
          {isAuthenticated ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  className="flex items-center gap-2 px-2 py-1 h-auto"
                  data-testid="user-menu-trigger"
                >
                  <Avatar className="h-8 w-8">
                    <AvatarFallback className="bg-brand text-white text-sm">
                      {getUserInitials()}
                    </AvatarFallback>
                  </Avatar>
                  <span className="hidden sm:inline text-sm text-slate-700 dark:text-slate-300">
                    {getUserName()}
                  </span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel className="font-normal">
                  <div className="flex flex-col space-y-1">
                    <p className="text-sm font-medium leading-none">
                      My Account
                    </p>
                    <p className="text-xs leading-none text-muted-foreground">
                      {getUserName()}
                    </p>
                    <p className="text-xs leading-none text-muted-foreground mt-1">
                      {user?.email}
                    </p>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={() => navigate("/profile")}
                  data-testid="profile-settings-button"
                >
                  <Settings className="mr-2 h-4 w-4" />
                  <span>Profile Settings</span>
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={handleLogout}
                  data-testid="logout-button"
                >
                  <LogOut className="mr-2 h-4 w-4" />
                  <span>Log out</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  const backendUrl =
                    process.env.REACT_APP_BACKEND_URL ||
                    "http://localhost:8000";
                  window.location.href = `${backendUrl}/api/scalekit/login`;
                }}
                className="flex items-center gap-2"
                data-testid="login-button"
              >
                <LogIn className="h-4 w-4" />
                <span className="hidden sm:inline">Login</span>
              </Button>
              <Button
                variant="default"
                size="sm"
                onClick={() => {
                  const backendUrl =
                    process.env.REACT_APP_BACKEND_URL ||
                    "http://localhost:8000";
                  window.location.href = `${backendUrl}/api/scalekit/login`;
                }}
                className="flex items-center gap-2"
                data-testid="register-button"
              >
                <UserPlus className="h-4 w-4" />
                <span className="hidden sm:inline">Sign Up</span>
              </Button>
            </div>
          )}
        </div>
      </div>
    </nav>
  );
};

export default Navigation;
